import React, { useMemo, ChangeEvent } from "react";
import { Control, RegisterOptions } from "react-hook-form";

import { INumberFieldProps, IUnitType } from "shared/types";
import { Distance, DistanceUnit, Unit, Weight, WeightUnit, WeightUnits } from "shared/utils";

import { FieldWrapperProps } from "../FieldWrapper";
import { NumberField } from "../NumberField/NumberField";
import { DimensionFieldContainer, DimensionFieldContainerProps } from "./styles";

interface DimensionFieldProps
  extends FieldWrapperProps,
    INumberFieldProps,
    DimensionFieldContainerProps {
  value?: Weight | Distance;
  control: Control;
  validation?: RegisterOptions;
  unit?: {
    metric: Unit;
    imperial: Unit;
  };
  unitType?: IUnitType;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  autocomplete?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onClick?: () => void;
}

export const DimensionField: React.FC<DimensionFieldProps> = ({
  value,
  unit,
  error,
  unitType = IUnitType.metric,
  allowNegative = false,
  isFullWidth,
  style,
  ...rest
}) => {
  const currentUnit = unit?.[unitType] || WeightUnits.kg;
  const label = currentUnit.getLabel();

  const currentValue = useMemo(() => {
    // convert defaultValue to current unit
    if (value) {
      return value.convert(currentUnit);
    }

    // defaultValue not defined, return null as value
    return currentUnit instanceof WeightUnit
      ? new Weight(null, currentUnit)
      : new Distance(null, currentUnit);
  }, [currentUnit, value]);

  if (!currentUnit) {
    return null;
  }

  const formatValue = (value: number) => {
    if (currentUnit instanceof DistanceUnit) {
      return new Distance(Number(value), currentUnit);
    }

    if (currentUnit instanceof WeightUnit) {
      return new Weight(Number(value), currentUnit);
    }

    return value;
  };

  return (
    <DimensionFieldContainer isFullWidth={isFullWidth} style={style}>
      <NumberField
        defaultValue={currentValue}
        value={currentValue.value !== null ? currentValue.value : ""}
        suffix={label}
        decimalScale={currentUnit.getPrecision()}
        formatValue={formatValue}
        allowNegative={allowNegative}
        {...rest}
      />
    </DimensionFieldContainer>
  );
};
